"use client";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Check, ChevronDown, AlertTriangle, X } from "lucide-react";
import { useState, useMemo, useCallback } from "react";
import { cn } from "../lib/utils.js";
import { getProviderIcon } from "@llmgateway/shared/components";
import { Badge } from "./ui/badge.js";
import { Button } from "./ui/button.js";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList, } from "./ui/command.js";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover.js";
function getStabilityBadgeProps(stability) {
    switch (stability) {
        case "beta":
            return {
                variant: "secondary",
                className: "text-blue-600",
                label: "BETA",
            };
        case "unstable":
            return {
                variant: "destructive",
                className: "text-red-600",
                label: "UNSTABLE",
            };
        case "experimental":
            return {
                variant: "destructive",
                className: "text-orange-600",
                label: "EXPERIMENTAL",
            };
        default:
            return null;
    }
}
function shouldShowStabilityWarning(stability) {
    return stability && ["unstable", "experimental"].includes(stability);
}
function isApiModel(model) {
    return "mappings" in model;
}
function getModelProviders(model) {
    if (isApiModel(model)) {
        return model.mappings.map(function (m) { return ({
            providerId: m.providerId,
            modelName: m.modelName,
            stability: m.stability,
        }); });
    }
    return model.providers.map(function (p) { return ({
        providerId: p.providerId,
        modelName: p.modelName,
        stability: p.stability,
    }); });
}
function getModelReleasedAt(model) {
    var _a;
    if (isApiModel(model)) {
        return (_a = model.releasedAt) !== null && _a !== void 0 ? _a : undefined;
    }
    return model.releasedAt;
}
function getMostUnstableStability(model) {
    var stabilityLevels = [
        "experimental",
        "unstable",
        "beta",
        "stable",
    ];
    var providers = getModelProviders(model);
    var allStabilities = __spreadArray([
        model.stability
    ], providers.map(function (p) { var _a; return (_a = p.stability) !== null && _a !== void 0 ? _a : model.stability; }), true).filter(Boolean);
    for (var _i = 0, stabilityLevels_1 = stabilityLevels; _i < stabilityLevels_1.length; _i++) {
        var level = stabilityLevels_1[_i];
        if (allStabilities.includes(level)) {
            return level;
        }
    }
    return undefined;
}
export function MultiModelSelector(_a) {
    var models = _a.models, providers = _a.providers, selectedModels = _a.selectedModels, onModelsChange = _a.onModelsChange, _b = _a.placeholder, placeholder = _b === void 0 ? "Select models..." : _b;
    var _c = useState(false), open = _c[0], setOpen = _c[1];
    var getProviderInfo = useCallback(function (providerId) {
        return providers.find(function (p) { return p.id === providerId; });
    }, [providers]);
    var modelsWithProviderInfo = useMemo(function () {
        return __spreadArray([], models, true).sort(function (a, b) {
            var dateA = getModelReleasedAt(a)
                ? new Date(getModelReleasedAt(a)).getTime()
                : 0;
            var dateB = getModelReleasedAt(b)
                ? new Date(getModelReleasedAt(b)).getTime()
                : 0;
            return dateB - dateA;
        })
            .map(function (model) {
            var modelProviders = getModelProviders(model);
            return __assign(__assign({}, model), { providersWithInfo: modelProviders.map(function (provider) { return (__assign(__assign({}, provider), { providerInfo: getProviderInfo(provider.providerId) })); }) });
        });
    }, [models, getProviderInfo]);
    var handleModelToggle = useCallback(function (modelId) {
        var isSelected = selectedModels.includes(modelId);
        if (isSelected) {
            onModelsChange(selectedModels.filter(function (id) { return id !== modelId; }));
        }
        else {
            onModelsChange(__spreadArray(__spreadArray([], selectedModels, true), [modelId], false));
        }
    }, [selectedModels, onModelsChange]);
    var removeModel = useCallback(function (modelId) {
        onModelsChange(selectedModels.filter(function (id) { return id !== modelId; }));
    }, [selectedModels, onModelsChange]);
    return (_jsxs("div", { className: "space-y-2", children: [selectedModels.length > 0 && (_jsx("div", { className: "flex flex-wrap gap-1", children: selectedModels.map(function (modelId) {
                    var _a, _b;
                    var model = modelsWithProviderInfo.find(function (m) { return m.id === modelId; });
                    var firstProvider = model === null || model === void 0 ? void 0 : model.providersWithInfo[0];
                    return (_jsxs(Badge, { variant: "secondary", className: "flex items-center gap-1", children: [((_a = firstProvider === null || firstProvider === void 0 ? void 0 : firstProvider.providerInfo) === null || _a === void 0 ? void 0 : _a.color) && (_jsx("div", { className: "w-2 h-2 rounded-full", style: {
                                    backgroundColor: firstProvider.providerInfo.color,
                                } })), (_b = model === null || model === void 0 ? void 0 : model.name) !== null && _b !== void 0 ? _b : modelId, _jsx(Button, { variant: "ghost", size: "sm", className: "h-3 w-3 p-0 hover:bg-transparent", onClick: function () { return removeModel(modelId); }, children: _jsx(X, { className: "h-2 w-2" }) })] }, modelId));
                }) })), _jsxs(Popover, { open: open, onOpenChange: setOpen, children: [_jsx(PopoverTrigger, { asChild: true, children: _jsxs(Button, { variant: "outline", role: "combobox", "aria-expanded": open, className: "w-full justify-between", children: [_jsx("span", { className: "text-left truncate", children: selectedModels.length === 0
                                        ? placeholder
                                        : "".concat(selectedModels.length, " model").concat(selectedModels.length === 1 ? "" : "s", " selected") }), _jsx(ChevronDown, { className: "ml-2 h-4 w-4 shrink-0 opacity-50" })] }) }), _jsx(PopoverContent, { className: "w-80 p-0 max-h-[400px]", align: "start", children: _jsxs(Command, { className: "flex flex-col", children: [_jsx(CommandInput, { placeholder: "Search models..." }), _jsx("div", { className: "max-h-[300px] overflow-y-auto", children: _jsxs(CommandList, { className: "overflow-y-scroll", children: [_jsx(CommandEmpty, { children: "No models found." }), modelsWithProviderInfo.map(function (model) {
                                                var _a, _b;
                                                var isSelected = selectedModels.includes(model.id);
                                                var mostUnstableStability = getMostUnstableStability(model);
                                                var stabilityProps = getStabilityBadgeProps(mostUnstableStability);
                                                return (_jsxs(CommandItem, { value: "".concat(model.id, " ").concat((_a = model.name) !== null && _a !== void 0 ? _a : ""), onSelect: function () { return handleModelToggle(model.id); }, className: "flex items-center justify-between py-3 cursor-pointer", children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx("div", { className: "flex items-center gap-1", children: model.providersWithInfo
                                                                        .slice(0, 3)
                                                                        .map(function (provider) {
                                                                        var ProviderIcon = getProviderIcon(provider.providerId);
                                                                        return ProviderIcon ? (_jsx(ProviderIcon, { className: "h-4 w-4" }, "".concat(provider.providerId, "-").concat(provider.modelName))) : null;
                                                                    }) }), _jsx("span", { className: "font-medium", children: (_b = model.name) !== null && _b !== void 0 ? _b : model.id }), shouldShowStabilityWarning(mostUnstableStability) && (_jsx(AlertTriangle, { className: "h-4 w-4 text-orange-500" }))] }), _jsxs("div", { className: "flex items-center gap-2", children: [stabilityProps && (_jsx(Badge, { variant: stabilityProps.variant, className: cn("text-xs px-1 py-0", stabilityProps.className), children: stabilityProps.label })), isSelected && (_jsx(Check, { className: "h-4 w-4 text-green-600" }))] })] }, model.id));
                                            })] }) })] }) })] })] }));
}
//# sourceMappingURL=multi-model-selector.js.map